/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import play from "@/assets/svg/play.svg";
import pause from "@/assets/svg/pause.svg";
import mute from "@/assets/svg/mute.svg";
import unmute from "@/assets/svg/unmute.svg";

type Props = {
  src?: string | { path?: string };
  restricted?: boolean;
  className?: string;
};

const NewAudioPlayer: React.FC<Props> = ({
  src = "https://share33.com/2024-tracks/Sia%20and%20Paris%20Hilton%20-%20Fame%20Wont%20Love%20You%20-%20(SongsLover.com).mp3",
  restricted = false,
  className,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => {
      setDuration(audio.duration);
      setCurrentTime(audio.currentTime);
    };

    const setAudioTime = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleAudioEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener("loadedmetadata", setAudioData);
    audio.addEventListener("timeupdate", setAudioTime);
    audio.addEventListener("ended", handleAudioEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", setAudioData);
      audio.removeEventListener("timeupdate", setAudioTime);
      audio.removeEventListener("ended", handleAudioEnded);
    };
  }, [isDragging]);

  const handleProgressChange = useCallback(
    (newPercentage: number) => {
      const audio = audioRef.current;
      if (!audio || restricted) return;

      const newTime = newPercentage * audio.duration;
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [restricted],
  );

  const calculatePercentage = useCallback((clientX: number) => {
    const progress = progressRef.current;
    if (!progress) return 0;

    const rect = progress.getBoundingClientRect();
    const x = clientX - rect.left;
    return Math.min(Math.max(x / rect.width, 0), 1);
  }, []);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (restricted) return;
    setIsDragging(true);
    handleProgressChange(calculatePercentage(event.clientX));
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (restricted) return;
    setIsDragging(true);
    handleProgressChange(calculatePercentage(event.touches[0].clientX));
  };

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) return;
      handleProgressChange(calculatePercentage(event.clientX));
    },
    [isDragging, calculatePercentage, handleProgressChange],
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      if (!isDragging) return;
      handleProgressChange(calculatePercentage(event.touches[0].clientX));
    },
    [isDragging, calculatePercentage, handleProgressChange],
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleTouchMove]);

  const handleVolumeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const audio = audioRef.current;
      if (!audio) return;

      const newVolume = parseFloat(event.target.value);
      audio.volume = newVolume;
      setVolume(newVolume);
    },
    [],
  );

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setShowVolumeSlider(!showVolumeSlider);
    if (volume > 0) {
      audio.volume = 0;
      setVolume(0);
    } else {
      audio.volume = 1;
      setVolume(1);
    }
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      if (restricted) return;
      audio.pause();
    } else {
      audio
        .play()
        .catch((error) => console.error("Audio playback failed:", error));
    }
    setIsPlaying(!isPlaying);
    setHasStarted(true);
  };

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, []);

  const formattedCurrentTime = useMemo(
    () => formatTime(currentTime),
    [currentTime, formatTime],
  );
  const formattedDuration = useMemo(
    () => formatTime(duration),
    [duration, formatTime],
  );

  return (
    <div
      className={`bg-white w-full relative rounded-[15px] h-[50px] flex flex-row gap-4 px-4 md:px-6 items-center ${className}`}
    >
      <audio
        ref={audioRef}
        src={typeof src === "string" ? src : src?.path}
        preload="metadata"
      >
        <track
          src="captions_en.vtt"
          kind="captions"
          label="english_captions"
          srcLang="en"
        />
      </audio>

      {!hasStarted ? (
        <button
          onClick={togglePlayPause}
          className="flex h-full w-full justify-center items-center text-black text-base font-semibold"
        >
          Click to start audio
        </button>
      ) : (
        <div className="w-full flex items-center gap-1 md:gap-4">
          <div className="flex-grow flex justify-start items-center gap-2">
            <span className="w-[90px] md:w-[90px] md:max-w-[90px] text-sm text-black">{`${formattedCurrentTime} / ${formattedDuration}`}</span>
            <div
              ref={progressRef}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              className={`${
                restricted ? "cursor-not-allowed" : "cursor-pointer"
              } w-full flex-1 mr-2 bg-gray-200 rounded-full h-1 overflow-hidden`}
            >
              <div
                className="bg-[#8065C9] h-1 rounded-full transition-all duration-100 ease-in-out"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              ></div>
            </div>
          </div>
          {!restricted && (
            <button
              onClick={togglePlayPause}
              className="text-black flex justify-center items-center gap-4 focus:outline-none transition-colors duration-200"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <img
                  src={pause}
                  className="w-4 md:w-[22px] mx-1 h-[22px]"
                  alt="pause"
                />
              ) : (
                <img
                  src={play}
                  className="w-4 md:w-[22px] mx-1 h-[22px]"
                  alt="play"
                />
              )}
            </button>
          )}
          <div>
            <button
              onClick={toggleMute}
              className="text-black flex justify-center items-center gap-4 focus:outline-none transition-colors duration-200"
              aria-label={volume > 0 ? "Mute" : "Unmute"}
            >
              {volume > 0 ? (
                <img
                  src={unmute}
                  className="w-4 md:w-[22px] mx-1 h-[22px]"
                  alt="unmute"
                />
              ) : (
                <img
                  src={mute}
                  className="w-4 md:w-[22px] mx-1 h-[22px]"
                  alt="mute"
                />
              )}
            </button>
            {showVolumeSlider && (
              <button
                className="absolute w-28 bottom-10 right-0 mb-2 bg-white px-2 pt-1 pb-2 rounded-[10px] shadow-md"
                onMouseEnter={() => setShowVolumeSlider(true)}
                onMouseLeave={() => setShowVolumeSlider(false)}
              >
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-24 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewAudioPlayer;
